/**
 * LanguageContext - Multi-language support for the UI
 *
 * Features:
 * - Turkish, English, Russian, German, French, and Italian support
 * - Persist language preference in localStorage
 * - Provide translations via useLanguage hook
 * - Translations organized in separate files under /locales
 *
 * To add a new language:
 * 1. Create a new translation file in /locales (e.g., es.ts)
 * 2. Add it to /locales/index.ts
 * 3. TypeScript will ensure all keys are translated
 */

import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import type { Language, TranslationKeys } from "../locales";

type Dict = Record<string, string>;
let loaded: Record<Language, Dict> | null = null;
let loading: Promise<void> | null = null;

/**
 * The UI strings (~350 KB) load with the app, not with the marketing pages,
 * which take their text from content/ and never call t(). AppRouter's lazy
 * routes wait for this before rendering, so t() is ready wherever it is used.
 */
export function loadTranslations(): Promise<void> {
  loading ??= import("../locales").then((m) => {
    loaded = m.translations;
  });
  return loading;
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  // Accept any string for forward-compat with keys that don't yet exist in the
  // typed union (e.g. dynamic integration labels). Falls back to returning the
  // key verbatim, so a typo is visible in the UI rather than a build failure.
  t: (key: TranslationKeys | string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/** Language when nothing is saved. Tests set VITE_DEFAULT_LANGUAGE=en (vite.config.ts). */
const DEFAULT_LANGUAGE: Language = import.meta.env.VITE_DEFAULT_LANGUAGE === "en" ? "en" : "tr";

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("language") as Language;
      if (saved && (saved === "tr" || saved === "en" || saved === "ru" || saved === "de" || saved === "fr" || saved === "it")) {
        return saved;
      }
      // No saved choice: Turkish. Hümo sells in Turkey, and search engines
      // render with an English browser; detecting navigator.language made
      // Google index the English copy under Turkish titles. Visitors switch
      // language from the menu and the choice is saved.
      return DEFAULT_LANGUAGE;
    }
    return DEFAULT_LANGUAGE;
  });

  useEffect(() => {
    localStorage.setItem("language", language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang: Language) => {
    console.log("🌍 Language changing from", language, "to", lang);
    setLanguageState(lang);
  };

  const t = (key: TranslationKeys | string): string => {
    return loaded?.[language]?.[key] || loaded?.en[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
